import React, { useState } from 'react';
import EditLanguagesModal from './EditLanguagesModal';

const ClientLanguages = ({ client, submissions, onRefresh }) => {
    const [editingSubmissionId, setEditingSubmissionId] = useState(null);
    const [editingLanguage, setEditingLanguage] = useState(null);

    const fromSubmissions = submissions
        .map(sub => ({ data: sub.language_info, submissionId: sub.id }))
        .filter(item => item.data && Object.keys(item.data).length > 0);

    const fromClient = Array.isArray(client?.language_data) ? client.language_data.map(data => ({ data, submissionId: null })) : [];

    const languageData = [...fromClient, ...fromSubmissions];

    if (languageData.length === 0) {
        return (
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                    <h3 className="text-lg font-bold text-white uppercase tracking-wider">🗣️ Language Skills</h3>
                </div>
                <div className="p-8 text-center text-gray-400">
                    No language skills information available from wizard submissions.
                </div>
            </div>
        );
    }

    return (
        <>
            {editingSubmissionId && editingLanguage && (
                <EditLanguagesModal
                    submissionId={editingSubmissionId}
                    languageData={editingLanguage}
                    onClose={() => {
                        setEditingSubmissionId(null);
                        setEditingLanguage(null);
                    }}
                    onSave={onRefresh}
                />
            )}

            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                    <h3 className="text-lg font-bold text-white uppercase tracking-wider">🗣️ Language Skills</h3>
                </div>
                <div className="p-6 space-y-6">
                    {languageData.map((item, index) => (
                        <div key={index} className="p-4 bg-gray-700/50 rounded border border-gray-600 relative">
                            {item.submissionId && (
                                <button
                                    onClick={() => {
                                        setEditingSubmissionId(item.submissionId);
                                        setEditingLanguage(item.data);
                                    }}
                                    className="absolute top-4 right-4 text-xs px-3 py-1.5 rounded bg-blue-600 text-white border border-blue-500 hover:bg-blue-700"
                                >
                                    ✏️ Edit
                                </button>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* English Test Scores */}
                                {item.data.englishTest && (
                                    <div className="md:col-span-2">
                                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">English Test</div>
                                        <div className="text-white font-mono font-bold mb-2">{item.data.englishTest}</div>

                                        {item.data.ieltsScores && (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                                                {item.data.ieltsScores.overall && (
                                                    <div className="p-2 bg-gray-800 rounded">
                                                        <div className="text-xs text-gray-500">Overall</div>
                                                        <div className="text-lg font-bold text-blue-400">{item.data.ieltsScores.overall}</div>
                                                    </div>
                                                )}
                                                {item.data.ieltsScores.listening && (
                                                    <div className="p-2 bg-gray-800 rounded">
                                                        <div className="text-xs text-gray-500">Listening</div>
                                                        <div className="text-lg font-bold text-green-400">{item.data.ieltsScores.listening}</div>
                                                    </div>
                                                )}
                                                {item.data.ieltsScores.reading && (
                                                    <div className="p-2 bg-gray-800 rounded">
                                                        <div className="text-xs text-gray-500">Reading</div>
                                                        <div className="text-lg font-bold text-yellow-400">{item.data.ieltsScores.reading}</div>
                                                    </div>
                                                )}
                                                {item.data.ieltsScores.writing && (
                                                    <div className="p-2 bg-gray-800 rounded">
                                                        <div className="text-xs text-gray-500">Writing</div>
                                                        <div className="text-lg font-bold text-purple-400">{item.data.ieltsScores.writing}</div>
                                                    </div>
                                                )}
                                                {item.data.ieltsScores.speaking && (
                                                    <div className="p-2 bg-gray-800 rounded">
                                                        <div className="text-xs text-gray-500">Speaking</div>
                                                        <div className="text-lg font-bold text-pink-400">{item.data.ieltsScores.speaking}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {item.data.toeflScore && (
                                            <div className="mt-2">
                                                <div className="text-xs text-gray-500">TOEFL Score</div>
                                                <div className="text-lg font-bold text-blue-400">{item.data.toeflScore}</div>
                                            </div>
                                        )}

                                        {item.data.pteScore && (
                                            <div className="mt-2">
                                                <div className="text-xs text-gray-500">PTE Score</div>
                                                <div className="text-lg font-bold text-blue-400">{item.data.pteScore}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Other Languages */}
                                {item.data.otherLanguages && Array.isArray(item.data.otherLanguages) && item.data.otherLanguages.length > 0 && (
                                    <div className="md:col-span-2">
                                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Other Languages</div>
                                        <div className="flex flex-wrap gap-2">
                                            {item.data.otherLanguages.map((language, idx) => (
                                                <span key={idx} className="px-3 py-1 bg-gray-800 rounded-full text-sm text-white border border-gray-600">
                                                    {typeof language === 'string' ? language : language.name || JSON.stringify(language)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Native Language */}
                                {item.data.nativeLanguage && (
                                    <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Native Language</div>
                                        <div className="text-white font-mono">{item.data.nativeLanguage}</div>
                                    </div>
                                )}
                            </div>

                            {/* Display all other fields as JSON */}
                            {Object.keys(item.data).length > 5 && (
                                <details className="mt-4">
                                    <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300">View All Fields</summary>
                                    <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-gray-300 overflow-auto">
                                        {JSON.stringify(item.data, null, 2)}
                                    </pre>
                                </details>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default ClientLanguages;
